(() => {
  "use strict";

  class WelltaylPouchBuilder {
    constructor(root) {
      this.root = root;
      const configEl = root.querySelector("[data-wpb-config]");
      if (!configEl) return;
      try {
        this.config = JSON.parse(configEl.textContent);
      } catch {
        return;
      }
      this.products = new Map();
      (this.config.products || []).forEach((p) => this.products.set(String(p.variantId), p));
      this.qty = new Map();
      this.unlocked = false;
      this.prevCount = 0;
      this.busy = false;

      this.slots = root.querySelector("[data-wpb-slots]");
      this.summary = root.querySelector("[data-wpb-summary]");
      this.empty = root.querySelector("[data-wpb-empty]");
      this.giftRow = root.querySelector("[data-wpb-gift-row]");
      this.kitRow = root.querySelector("[data-wpb-kit-row]");
      this.status = root.querySelector("[data-wpb-status]");
      this.bar = root.querySelector("[data-wpb-bar]");
      this.barSlots = root.querySelector("[data-wpb-bar-slots]");
      this.barRow = root.querySelector("[data-wpb-bar-row]");

      this.pouchboxInView = true;
      // On mobile the builder card is hidden, so the sticky bar slides in as soon as the
      // hero photo is mostly scrolled away (falls back to the builder card if there's no hero).
      const barAnchor = root.querySelector("[data-wpb-heroband]") || root.querySelector("[data-wpb-pouchbox]");
      if (this.bar) {
        this.bar.hidden = false;
        if (barAnchor && "IntersectionObserver" in window) {
          new IntersectionObserver(
            ([entry]) => {
              this.pouchboxInView = entry.isIntersecting && entry.intersectionRatio > 0.35;
              this.bar.classList.toggle("is-visible", !this.pouchboxInView);
            },
            { threshold: [0, 0.35, 0.36, 1] }
          ).observe(barAnchor);
        }
      }

      this.infoModal = root.querySelector("[data-wpb-info-modal]");
      this.infoCache = new Map();
      this.infoOpener = null;
      this.infoHandle = null;
      if (this.infoModal) {
        // Warm the cache when the customer hovers/touches a "?" so the popup opens instantly.
        root.addEventListener("pointerover", (event) => {
          const info = event.target.closest("[data-wpb-info]");
          if (info) this.loadInfo(info.dataset.handle);
        });
        document.addEventListener("keydown", (event) => {
          if (event.key === "Escape" && !this.infoModal.hidden) this.closeInfo();
        });
      }

      root.addEventListener("click", (event) => this.onClick(event));
      document.addEventListener("click", (event) => this.onRemoteAdd(event));
      this.render();
      this.applyFilterFromUrl();
    }

    // ---- Product info popup ("?" on each card) ----------------------------------------------
    // Content is pulled from the product's own page (the Ingredients / Benefits / Best For /
    // How to Use accordions in the main product section), so there's nothing extra to maintain.
    loadInfo(handle) {
      if (!handle) return Promise.resolve([]);
      if (this.infoCache.has(handle)) return this.infoCache.get(handle);
      const root = (this.config.routesRoot || "/").replace(/\/$/, "");
      const promise = fetch(`${root}/products/${encodeURIComponent(handle)}`, { credentials: "same-origin" })
        .then((res) => {
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          return res.text();
        })
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, "text/html");
          const main =
            doc.querySelector('[id^="shopify-section-template--"][id$="__main"]') ||
            doc.querySelector(".product-info") ||
            doc.body;
          const sections = [];
          main.querySelectorAll("accordion-disclosure.accordion").forEach((acc) => {
            const summary = acc.querySelector(".accordion__summary");
            const content = acc.querySelector(".accordion__content");
            if (!summary || !content) return;
            summary.querySelectorAll("svg").forEach((svg) => svg.remove());
            const title = summary.textContent.replace(/\s+/g, " ").trim();
            content.querySelectorAll("script, style").forEach((el) => el.remove());
            const body = content.innerHTML.trim();
            if (title && body) sections.push({ title, body });
          });
          if (!sections.length) {
            const desc = main.querySelector(".product-info__description, .prose");
            if (desc) sections.push({ title: "About", body: desc.innerHTML.trim() });
          }
          return sections;
        })
        .catch(() => {
          this.infoCache.delete(handle);
          return null;
        });
      this.infoCache.set(handle, promise);
      return promise;
    }

    openInfo(button) {
      if (!this.infoModal) return;
      const { handle, title, url, image } = button.dataset;
      this.infoOpener = button;
      this.infoHandle = handle;

      const titleEl = this.infoModal.querySelector("[data-wpb-info-title]");
      const linkEl = this.infoModal.querySelector("[data-wpb-info-link]");
      const imageEl = this.infoModal.querySelector("[data-wpb-info-image]");
      const bodyEl = this.infoModal.querySelector("[data-wpb-info-body]");
      if (titleEl) titleEl.textContent = title || "";
      if (linkEl) {
        linkEl.href = url || "#";
        linkEl.hidden = !url;
      }
      if (imageEl) {
        imageEl.hidden = !image;
        if (image) imageEl.src = image;
        imageEl.alt = title || "";
      }
      if (bodyEl) bodyEl.innerHTML = '<p class="wpb-info-modal__status">Loading…</p>';

      this.infoModal.hidden = false;
      this.scrollLock = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      window.requestAnimationFrame(() => {
        this.infoModal.classList.add("is-open");
        const panel = this.infoModal.querySelector("[data-wpb-info-panel]");
        if (panel) panel.focus({ preventScroll: true });
      });

      this.loadInfo(handle).then((sections) => {
        if (this.infoHandle !== handle || !bodyEl) return;
        if (!sections) {
          bodyEl.innerHTML =
            '<p class="wpb-info-modal__status">Couldn’t load the details. Please try again or view the full product page.</p>';
          return;
        }
        if (!sections.length) {
          bodyEl.innerHTML = '<p class="wpb-info-modal__status">See the full product page for details.</p>';
          return;
        }
        bodyEl.innerHTML = sections
          .map(
            (s) =>
              `<section class="wpb-info-modal__section"><h4 class="wpb-info-modal__section-title">${s.title}</h4><div class="wpb-info-modal__prose">${s.body}</div></section>`
          )
          .join("");
        bodyEl.scrollTop = 0;
      });
    }

    closeInfo() {
      if (!this.infoModal || this.infoModal.hidden) return;
      this.infoModal.classList.remove("is-open");
      this.infoHandle = null;
      document.body.style.overflow = this.scrollLock || "";
      const opener = this.infoOpener;
      this.infoOpener = null;
      window.setTimeout(() => {
        this.infoModal.hidden = true;
        if (opener && document.contains(opener)) opener.focus({ preventScroll: true });
      }, 200);
    }

    // Deep link: /products/build-your-pouch?concern=<collection-handle> preselects that filter
    // (used by the "shop by concern" links in the header menu).
    applyFilterFromUrl() {
      const concern = new URLSearchParams(window.location.search).get("concern");
      if (!concern) return;
      const button = this.root.querySelector(`[data-wpb-filter="${concern.replace(/["\\]/g, "")}"]`);
      if (!button) return;
      this.applyFilter(button);
      window.setTimeout(() => this.scrollToProducts(), 150);
    }

    format(cents) {
      const value = (cents / 100).toFixed(2);
      return `$${value}`;
    }

    get totalCount() {
      let count = 0;
      this.qty.forEach((q) => (count += q));
      return count;
    }

    get totals() {
      let total = 0;
      let compare = 0;
      this.qty.forEach((q, id) => {
        const p = this.products.get(id);
        if (!p) return;
        total += p.price * q;
        compare += (p.compareAt > p.price ? p.compareAt : p.price) * q;
      });
      return { total, compare };
    }

    // The product lines that will actually be sent to the cart (kit components are swapped for the kit variant).
    cartLines(kitInfo) {
      const kitIds = new Set(kitInfo.active ? kitInfo.ids : []);
      const lines = [];
      this.qty.forEach((q, id) => {
        const quantity = kitIds.has(id) ? q - 1 : q;
        const p = this.products.get(id);
        if (quantity > 0 && p) lines.push({ id: Number(id), quantity, price: p.price });
      });
      return lines;
    }

    // Preview of the "Bundle X% off" automatic discounts configured in Shopify Admin. Shopify counts the
    // quantity of eligible items and floors the discount per line, so we do the same to match to the cent.
    discountState(kitInfo) {
      const none = { percent: 0, amount: 0, eligibleQty: 0 };
      const tiers = (this.config.discounts || []).filter((t) => t && t.minQty > 0 && t.percent > 0);
      if (!tiers.length) return none;
      const lines = this.cartLines(kitInfo);
      const eligibleQty = lines.reduce((sum, line) => sum + line.quantity, 0);
      const tier = tiers
        .filter((t) => eligibleQty >= t.minQty)
        .sort((a, b) => b.percent - a.percent)[0];
      if (!tier) return { ...none, eligibleQty };
      const amount = lines.reduce((sum, line) => sum + Math.floor((line.price * line.quantity * tier.percent) / 100), 0);
      return { percent: tier.percent, amount, eligibleQty };
    }

    kitState() {
      const kit = this.config.kit || {};
      const none = { allIn: false, active: false, ids: [], savings: 0 };
      if (!kit.enabled || !kit.variantId || !Array.isArray(kit.handles)) return none;
      const ids = [];
      let sum = 0;
      for (const handle of kit.handles) {
        let match = null;
        this.products.forEach((p) => {
          if (p.handle === handle) match = p;
        });
        if (!match) return none;
        ids.push(String(match.variantId));
        sum += match.price;
      }
      const allIn = ids.every((id) => (this.qty.get(id) || 0) >= 1);
      const savings = sum - kit.price;
      const base = { allIn, active: false, ids, savings };
      if (!allIn || savings <= 0) return base;

      // The kit isn't part of the volume-discount collection, so swapping the components for the kit
      // removes them from the 10%/15% count. Only swap when the kit path actually saves the customer
      // more than leaving the individual products in the cart with the volume discount applied.
      const savingsWithKit = savings + this.discountState({ ...base, active: true }).amount;
      const savingsWithoutKit = this.discountState(base).amount;
      return { ...base, active: savingsWithKit >= savingsWithoutKit };
    }

    onClick(event) {
      const add = event.target.closest("[data-wpb-add]");
      const routineAdd = event.target.closest("[data-wpb-add-routine]");
      const plus = event.target.closest("[data-wpb-plus]");
      const minus = event.target.closest("[data-wpb-minus]");
      const remove = event.target.closest("[data-wpb-remove]");
      const cta = event.target.closest("[data-wpb-cta], [data-wpb-bar-cta]");
      const filter = event.target.closest("[data-wpb-filter]");
      const slotAdd = event.target.closest("[data-wpb-slot-add]");
      const slotRemove = event.target.closest("[data-wpb-slot-remove]");
      const info = event.target.closest("[data-wpb-info]");
      const infoClose = event.target.closest("[data-wpb-info-close]");

      if (info) {
        event.preventDefault();
        event.stopPropagation();
        this.openInfo(info);
        return;
      }

      if (infoClose) {
        event.preventDefault();
        this.closeInfo();
        return;
      }

      if (filter) {
        this.applyFilter(filter);
        return;
      }

      if (slotRemove) {
        event.preventDefault();
        event.stopPropagation();
        this.change(slotRemove.dataset.variantId, -1);
        return;
      }

      if (routineAdd) {
        const kit = this.config.kit || {};
        if (this.kitState().allIn) {
          // Customer changed their mind: take one of each kit component back out.
          (kit.handles || []).forEach((handle) => {
            this.products.forEach((p) => {
              const id = String(p.variantId);
              if (p.handle !== handle) return;
              const current = this.qty.get(id) || 0;
              if (current <= 1) this.qty.delete(id);
              else this.qty.set(id, current - 1);
            });
          });
        } else {
          (kit.handles || []).forEach((handle) => {
            this.products.forEach((p) => {
              const id = String(p.variantId);
              if (p.handle === handle && p.available && !(this.qty.get(id) >= 1)) this.qty.set(id, 1);
            });
          });
        }
        this.render();
        return;
      }

      if (slotAdd) {
        this.scrollToProducts();
        return;
      }

      // Empty pouch: the bar's button doubles as "go pick products"
      if (cta && cta.hasAttribute("data-wpb-bar-cta") && this.totalCount === 0) {
        this.scrollToProducts();
        return;
      }

      if (add || plus) {
        const card = event.target.closest("[data-wpb-card]");
        if (card) this.change(card.dataset.variantId, 1);
      } else if (minus) {
        const card = event.target.closest("[data-wpb-card]");
        if (card) this.change(card.dataset.variantId, -1);
      } else if (remove) {
        this.setQty(remove.dataset.variantId, 0);
      } else if (cta) {
        this.submit();
      }
    }

    scrollToProducts() {
      const isMobile = window.matchMedia("(max-width: 999px)").matches;
      const grid = this.root.querySelector(".wpb-grid");
      const heading = this.root.querySelector(".wpb-products__heading");
      // Mobile: jump straight to the product cards (the heading sits right under the hero,
      // so it's usually already on screen when the sticky bar is showing).
      const target = (isMobile ? grid || heading : heading || grid) || this.root.querySelector(".wpb-products");
      if (!target) return;
      const rootStyle = getComputedStyle(document.documentElement);
      const headerSticky = rootStyle.getPropertyValue("--header-is-sticky").trim() === "1";
      const headerOffset = headerSticky ? parseFloat(rootStyle.getPropertyValue("--header-height")) || 0 : 0;
      const top = target.getBoundingClientRect().top + window.scrollY - headerOffset - 12;
      window.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
    }

    onRemoteAdd(event) {
      const button = event.target.closest("[data-wpb-remote-add]");
      if (!button || this.root.contains(button)) return;
      let match = null;
      this.products.forEach((p) => {
        if (p.handle === button.dataset.handle) match = p;
      });
      if (!match || !match.available) return;
      this.change(match.variantId, 1);

      const label = button.querySelector("[data-wpb-remote-label]") || button;
      if (!button.dataset.label) button.dataset.label = label.textContent.trim();
      button.classList.add("is-added");
      label.textContent = "Added to pouch \u2713";
      window.clearTimeout(button._wpbAddedTimer);
      button._wpbAddedTimer = window.setTimeout(() => {
        button.classList.remove("is-added");
        label.textContent = button.dataset.label;
      }, 1600);
    }

    applyFilter(button) {
      const value = button.dataset.wpbFilter;
      if (button.classList.contains("is-active")) return;
      this.root.querySelectorAll("[data-wpb-filter]").forEach((pill) => {
        pill.classList.toggle("is-active", pill === button);
      });
      // If a previous filter swap is still mid-flight, settle it first.
      if (this.filterPending) {
        window.clearTimeout(this.filterTimer);
        this.filterPending();
      }

      const grid = this.root.querySelector(".wpb-grid");
      const cards = Array.from(this.root.querySelectorAll("[data-wpb-card]"));
      const state = cards.map((card) => {
        const concerns = (card.dataset.concerns || "").split(/\s+/).filter(Boolean);
        return {
          card,
          wasVisible: !card.classList.contains("is-filtered-out"),
          visible: value === "all" || concerns.includes(value)
        };
      });
      const leaving = state.filter((s) => s.wasVisible && !s.visible);
      const entering = state.filter((s) => !s.wasVisible && s.visible);
      const staying = state.filter((s) => s.wasVisible && s.visible);

      // Remember where the remaining cards sit so they can glide to their new spot.
      const before = new Map(staying.map((s) => [s.card, s.card.getBoundingClientRect()]));

      // Step 1: fade out the cards that don't match.
      leaving.forEach((s) => s.card.classList.add("is-leaving"));

      const finish = () => {
        this.filterPending = null;
        leaving.forEach((s) => {
          s.card.classList.remove("is-leaving");
          s.card.classList.add("is-filtered-out");
        });
        entering.forEach((s, i) => {
          s.card.classList.remove("is-filtered-out", "is-entering");
          s.card.style.setProperty("--wpb-i", Math.min(i, 8));
        });

        // Step 2 (FLIP): remaining cards slide from their old position to the new one.
        staying.forEach((s) => {
          const prev = before.get(s.card);
          const next = s.card.getBoundingClientRect();
          const dx = prev.left - next.left;
          const dy = prev.top - next.top;
          s.card.classList.remove("is-moving");
          if (dx || dy) {
            s.card.style.transition = "none";
            s.card.style.transform = `translate(${dx}px, ${dy}px)`;
          }
        });
        if (grid) void grid.offsetWidth;
        staying.forEach((s) => {
          s.card.style.transition = "";
          s.card.classList.add("is-moving");
          s.card.style.transform = "";
        });

        // Step 3: newly matching cards fade in.
        entering.forEach((s) => s.card.classList.add("is-entering"));

        window.setTimeout(() => {
          staying.forEach((s) => s.card.classList.remove("is-moving"));
        }, 400);
      };

      if (leaving.length) {
        this.filterPending = finish;
        this.filterTimer = window.setTimeout(finish, 180);
      } else {
        finish();
      }
    }

    change(variantId, delta) {
      const current = this.qty.get(String(variantId)) || 0;
      this.setQty(variantId, Math.max(0, Math.min(10, current + delta)));
    }

    setQty(variantId, value) {
      const id = String(variantId);
      if (value <= 0) this.qty.delete(id);
      else this.qty.set(id, value);
      this.render();
    }

    render() {
      const count = this.totalCount;
      const { total, compare } = this.totals;
      const gift = this.config.gift || {};
      const giftActive = Boolean(gift.enabled && gift.variantId && gift.threshold);
      const nowUnlocked = giftActive && count >= gift.threshold;

      this.root.querySelectorAll("[data-wpb-card]").forEach((card) => {
        const q = this.qty.get(String(card.dataset.variantId)) || 0;
        const addButton = card.querySelector("[data-wpb-add]");
        const buyRow = card.querySelector("[data-wpb-buy]");
        const stepper = card.querySelector("[data-wpb-stepper]");
        const qtyEl = card.querySelector("[data-wpb-qty]");
        card.classList.toggle("is-added", q > 0);
        if (buyRow) buyRow.hidden = q > 0;
        else if (addButton) addButton.hidden = q > 0;
        if (stepper) stepper.hidden = q === 0;
        if (qtyEl) qtyEl.textContent = q;
      });

      this.renderSlots(count, giftActive ? gift.threshold : 0);
      this.renderProgress(count, gift, giftActive, nowUnlocked);
      this.renderSummary();

      if (this.empty) this.empty.hidden = count > 0;
      if (this.giftRow) this.giftRow.hidden = !nowUnlocked;

      const kitInfo = this.kitState();
      let displayTotal = total;
      let displayCompare = compare;
      if (kitInfo.active) {
        displayTotal = total - kitInfo.savings;
        displayCompare = Math.max(compare, total);
      }

      const discount = this.discountState(kitInfo);
      if (discount.amount > 0) {
        displayTotal -= discount.amount;
        displayCompare = Math.max(displayCompare, total);
      }
      const discountRow = this.root.querySelector("[data-wpb-discount-row]");
      if (discountRow) {
        discountRow.hidden = discount.amount <= 0;
        const titleEl = discountRow.querySelector("[data-wpb-discount-title]");
        const amountEl = discountRow.querySelector("[data-wpb-discount-amount]");
        if (titleEl) titleEl.textContent = `${discount.percent}% bundle discount`;
        if (amountEl) amountEl.textContent = this.format(discount.amount);
      }

      if (this.kitRow) {
        this.kitRow.hidden = !kitInfo.active;
        const savingsEl = this.kitRow.querySelector("[data-wpb-kit-savings]");
        if (savingsEl) savingsEl.textContent = this.format(kitInfo.savings);
      }

      this.root.querySelectorAll("[data-wpb-add-routine]").forEach((routineButton) => {
        routineButton.classList.toggle("is-added", kitInfo.allIn);
        const labels = routineButton.querySelectorAll("[data-wpb-routine-label]");
        (labels.length ? Array.from(labels) : [routineButton]).forEach((routineLabel) => {
          if (!routineLabel.dataset.label) routineLabel.dataset.label = routineLabel.textContent.trim();
          routineLabel.textContent = kitInfo.allIn
            ? routineLabel.dataset.addedLabel || "Remove Full Kit"
            : routineLabel.dataset.label;
        });
      });

      const compareVisible = displayCompare > displayTotal && count > 0;
      const totalEl = this.root.querySelector("[data-wpb-total]");
      const compareEl = this.root.querySelector("[data-wpb-compare]");
      if (totalEl) totalEl.textContent = this.format(displayTotal);
      if (compareEl) {
        compareEl.hidden = !compareVisible;
        compareEl.textContent = this.format(displayCompare);
      }

      const ctaButton = this.root.querySelector("[data-wpb-cta]");
      const ctaLabel = this.root.querySelector("[data-wpb-cta-label]");
      if (ctaButton) ctaButton.disabled = count === 0 || this.busy;
      if (ctaLabel) {
        ctaLabel.textContent = count === 0
          ? "Add products to start"
          : `${this.config.ctaLabel} \u00b7 ${this.format(displayTotal)}`;
      }

      if (this.bar) {
        this.bar.classList.toggle("is-empty", count === 0);
        const barInfo = this.bar.querySelector("[data-wpb-bar-info]");
        if (barInfo) barInfo.hidden = count === 0;
        const barTrust = this.bar.querySelector("[data-wpb-bar-trust]");
        if (barTrust) barTrust.hidden = count === 0;
        const barTotal = this.bar.querySelector("[data-wpb-bar-total]");
        const barCompare = this.bar.querySelector("[data-wpb-bar-compare]");
        const barLabel = this.bar.querySelector("[data-wpb-bar-label]");
        if (barTotal) barTotal.textContent = this.format(displayTotal);
        if (barCompare) {
          barCompare.hidden = !compareVisible;
          barCompare.textContent = this.format(displayCompare);
        }
        if (barLabel) {
          barLabel.textContent = count === 0 ? "Add products" : this.config.barCtaLabel || "Checkout";
        }
      }

      // Celebrate when the pouch, 10% and 15% tiles are reached (upward crossings only).
      const prevCount = this.prevCount || 0;
      if (giftActive && count > prevCount) {
        const milestones = [gift.threshold, ...(this.config.discounts || []).map((t) => t && t.minQty).filter(Boolean)];
        const crossed = milestones.filter((m) => prevCount < m && count >= m);
        if (crossed.length) this.burstConfetti(this.milestoneTile(Math.max(...crossed)));
      }
      this.prevCount = count;
      this.unlocked = nowUnlocked;
    }

    renderSlots(count, threshold) {
      const entries = [];
      this.qty.forEach((q, id) => {
        const p = this.products.get(id);
        if (p) entries.push({ product: p, qty: q });
      });
      const giftIcon = "<span class=\"wpb-slot__icon\" aria-hidden=\"true\"><svg viewBox=\"0 0 24 24\" fill=\"none\"><path d=\"M20 12v9H4v-9M2 7h20v5H2V7Zm10 14V7m0 0s-2.4 0-3.9-1.5C6.9 4.3 7 2 7 2s2.4 0 3.9 1.5C12.1 4.7 12 7 12 7Zm0 0s2.4 0 3.9-1.5C17.1 4.3 17 2 17 2s-2.4 0-3.9 1.5C11.9 4.7 12 7 12 7Z\" stroke=\"currentColor\" stroke-width=\"2\" stroke-linejoin=\"round\"/></svg></span>";
      // Tiles past the pouch-gift threshold advertise the volume discounts (mirrored from Shopify Admin).
      const discountTiers = (this.config.discounts || []).filter((t) => t && t.minQty > 0 && t.percent > 0);
      const lastTier = discountTiers.reduce((max, t) => Math.max(max, t.minQty), 0);
      const tileCount = Math.max(threshold, lastTier, count);
      [this.slots, this.barSlots].forEach((container) => {
        if (!container) return;
        container.innerHTML = "";
        entries.slice(0, 8).forEach((entry) => {
          const slot = document.createElement("span");
          slot.className = "wpb-slot is-filled";
          const img = document.createElement("img");
          img.src = entry.product.image || "";
          img.alt = "";
          img.loading = "lazy";
          slot.appendChild(img);
          if (entry.qty > 1) {
            const badge = document.createElement("i");
            badge.className = "wpb-slot__count";
            badge.textContent = `\u00d7${entry.qty}`;
            slot.appendChild(badge);
          }
          const removeBtn = document.createElement("button");
          removeBtn.type = "button";
          removeBtn.className = "wpb-slot__remove";
          removeBtn.setAttribute("data-wpb-slot-remove", "");
          removeBtn.dataset.variantId = String(entry.product.variantId);
          removeBtn.setAttribute("aria-label", `Remove ${entry.product.title} from your pouch`);
          removeBtn.innerHTML = "<svg viewBox=\"0 0 24 24\" fill=\"none\" stroke=\"currentColor\" stroke-width=\"2.5\" stroke-linecap=\"round\" aria-hidden=\"true\"><path d=\"M7 7l10 10M17 7L7 17\"/></svg>";
          slot.appendChild(removeBtn);
          container.appendChild(slot);
        });
        for (let i = count; i < tileCount; i += 1) {
          const position = i + 1;
          const placeholder = document.createElement("button");
          placeholder.type = "button";
          placeholder.className = "wpb-slot is-empty";
          placeholder.setAttribute("data-wpb-slot-add", "");
          const tier = discountTiers.find((t) => t.minQty === position);
          if (threshold > 0 && position === threshold) {
            placeholder.classList.add("wpb-slot--milestone", "wpb-slot--gift");
            placeholder.setAttribute("aria-label", `Add ${threshold} products to get the free pouch`);
            placeholder.innerHTML = `${giftIcon}<small>Free pouch</small>`;
          } else if (tier) {
            placeholder.classList.add("wpb-slot--milestone");
            placeholder.setAttribute("aria-label", `Add ${position} products for ${tier.percent}% off`);
            placeholder.innerHTML = `<strong>${tier.percent}%</strong><small>off</small>`;
          } else {
            placeholder.setAttribute("aria-label", "Add a product to your pouch");
            placeholder.innerHTML = "<i aria-hidden=\"true\">+</i><small>Add product</small>";
          }
          container.appendChild(placeholder);
        }
      });
    }

    renderProgress(count, gift, giftActive, nowUnlocked) {
      const wraps = this.root.querySelectorAll("[data-wpb-progress]");
      if (!giftActive) {
        wraps.forEach((wrap) => (wrap.hidden = true));
        return;
      }
      const remaining = Math.max(0, gift.threshold - count);
      let labelHtml;
      if (nowUnlocked) {
        labelHtml = `<strong>FREE ${this.escape(gift.title)} unlocked!</strong> It ships with your order.`;
      } else if (count === 0) {
        labelHtml = `Add ${gift.threshold} products and the ${this.escape(gift.title)} is FREE`;
      } else {
        labelHtml = `${remaining} more product${remaining === 1 ? "" : "s"} to unlock your FREE ${this.escape(gift.title)}`;
      }
      const percent = Math.min(100, Math.round((count / gift.threshold) * 100));
      const intro = !this.progressIntroDone;
      this.progressIntroDone = true;
      // The floating bar (mobile) and the panel's top progress (desktop) show the full offer
      // ("free pouch + 10–15% off") while the pouch is still empty.
      const barEmptyLabel = this.config.barEmptyLabel ? this.escape(this.config.barEmptyLabel) : "";
      wraps.forEach((wrap) => {
        wrap.hidden = false;
        wrap.classList.toggle("is-unlocked", nowUnlocked);
        wrap.classList.toggle("has-items", count > 0);
        const label = wrap.querySelector("[data-wpb-progress-label]");
        const fill = wrap.querySelector("[data-wpb-progress-fill]");
        const track = wrap.querySelector("[data-wpb-progress-track]");
        const isBar = this.bar && this.bar.contains(wrap);
        const showsOffer = isBar || wrap.classList.contains("wpb-progress--top");
        if (label) label.innerHTML = showsOffer && count === 0 && barEmptyLabel ? barEmptyLabel : labelHtml;
        if (fill) {
          if (intro) {
            // Start full, then drain down to the real value on first load.
            fill.classList.add("is-intro");
            fill.style.width = "100%";
            void fill.offsetWidth;
            window.setTimeout(() => {
              fill.style.width = `${percent}%`;
              window.setTimeout(() => fill.classList.remove("is-intro"), 1000);
            }, 350);
          } else {
            fill.style.width = `${percent}%`;
          }
        }
        if (track) track.setAttribute("aria-valuenow", Math.min(count, gift.threshold));
      });
    }

    renderSummary() {
      if (!this.summary) return;
      this.summary.innerHTML = "";
      this.qty.forEach((q, id) => {
        const p = this.products.get(id);
        if (!p) return;
        const li = document.createElement("li");
        li.className = "wpb-summary__item";
        li.innerHTML = `
          <img src="${p.image || ""}" alt="" width="48" height="48" loading="lazy">
          <span class="wpb-summary__title">${this.escape(p.title)}${q > 1 ? ` <em>\u00d7${q}</em>` : ""}</span>
          <span class="wpb-summary__price">${this.format(p.price * q)}</span>
          <button type="button" class="wpb-summary__remove" data-wpb-remove data-variant-id="${id}" aria-label="Remove ${this.escape(p.title)}">&times;</button>
        `;
        li.querySelector("[data-wpb-remove]").dataset.variantId = id;
        this.summary.appendChild(li);
      });
    }

    escape(value) {
      const div = document.createElement("div");
      div.textContent = value == null ? "" : String(value);
      return div.innerHTML;
    }

    // The slot tile that represents the n-th product, in whichever slot row is on screen.
    milestoneTile(position) {
      const container = [this.barSlots, this.slots].find((el) => el && el.getClientRects().length > 0);
      if (!container || !container.children.length) return null;
      return container.children[Math.min(position, container.children.length) - 1] || null;
    }

    burstConfetti(origin) {
      // Use whichever confetti layer is actually rendered: the builder card on desktop,
      // the full-screen overlay on mobile (where the card is hidden).
      const hosts = Array.from(this.root.querySelectorAll("[data-wpb-confetti], [data-wpb-confetti-screen]"));
      const host = hosts.find((el) => el.getClientRects().length > 0);
      if (!host) return;
      const colors = ["#2f6b4f", "#4c8a68", "#7fb69a", "#a8d5b9", "#d3ecdc"];

      // Burst out of the milestone tile when we have one; otherwise rain from the top.
      const fromTile = origin && origin.getClientRects().length > 0;
      let originX = 0;
      let originY = 0;
      if (fromTile) {
        const hostRect = host.getBoundingClientRect();
        const rect = origin.getBoundingClientRect();
        originX = rect.left + rect.width / 2 - hostRect.left;
        originY = rect.top + rect.height / 2 - hostRect.top;
      }

      const pieces = fromTile ? 30 : 26;
      for (let i = 0; i < pieces; i += 1) {
        const piece = document.createElement("span");
        piece.className = "wpb-confetti-piece";
        piece.style.background = colors[Math.floor(Math.random() * colors.length)];
        piece.style.setProperty("--wpb-spin", `${Math.round(Math.random() * 540 - 270)}deg`);
        if (fromTile) {
          const angle = Math.random() * Math.PI * 2;
          const distance = 45 + Math.random() * 85;
          piece.classList.add("wpb-confetti-piece--burst");
          piece.style.left = `${originX}px`;
          piece.style.top = `${originY}px`;
          piece.style.setProperty("--wpb-dx", `${Math.cos(angle) * distance}px`);
          piece.style.setProperty("--wpb-dy", `${Math.sin(angle) * distance - 45}px`);
          piece.style.animationDuration = `${0.7 + Math.random() * 0.45}s`;
          piece.style.animationDelay = `${Math.random() * 0.08}s`;
        } else {
          piece.style.left = `${Math.random() * 100}%`;
          piece.style.animationDuration = `${0.9 + Math.random() * 0.7}s`;
          piece.style.animationDelay = `${Math.random() * 0.25}s`;
          piece.style.setProperty("--wpb-drift", `${(Math.random() - 0.5) * 80}px`);
        }
        host.appendChild(piece);
        window.setTimeout(() => piece.remove(), 2000);
      }
    }

    async submit() {
      if (this.busy || this.totalCount === 0) return;
      this.busy = true;
      const buttons = this.root.querySelectorAll("[data-wpb-cta], [data-wpb-bar-cta]");
      buttons.forEach((button) => button.setAttribute("aria-busy", "true"));
      if (this.status) this.status.textContent = "Adding your pouch\u2026";

      const routesRoot = this.config.routesRoot || "/";
      const kitInfo = this.kitState();
      const items = this.cartLines(kitInfo).map(({ id, quantity }) => ({ id, quantity }));
      if (kitInfo.active) {
        items.push({
          id: Number(this.config.kit.variantId),
          quantity: 1,
          properties: { _welltayl_kit_upgrade: "true" }
        });
      }

      const gift = this.config.gift || {};
      if (gift.enabled && gift.variantId && this.totalCount >= gift.threshold) {
        try {
          const cart = await fetch(`${routesRoot}cart.js`).then((response) => response.json());
          const hasGift = (cart.items || []).some((line) => line.variant_id === Number(gift.variantId));
          if (!hasGift) {
            items.push({
              id: Number(gift.variantId),
              quantity: 1,
              properties: { _welltayl_auto_gift: "true" }
            });
          }
        } catch {
          // If the cart check fails, still add the gift; duplicates are rare and harmless here.
          items.push({
            id: Number(gift.variantId),
            quantity: 1,
            properties: { _welltayl_auto_gift: "true" }
          });
        }
      }

      try {
        const sections = new Set();
        document.documentElement.dispatchEvent(new CustomEvent("cart:prepare-bundled-sections", {
          bubbles: true,
          detail: { sections }
        }));
        const response = await fetch(`${routesRoot}cart/add.js`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Requested-With": "XMLHttpRequest"
          },
          body: JSON.stringify({ items, sections: [...sections].join(",") })
        });
        const json = await response.json();
        if (!response.ok) throw new Error(json.description || json.message || "Unable to add to cart");

        document.dispatchEvent(new CustomEvent("welltayl:pouch_add_to_cart", {
          detail: { itemCount: this.totalCount, items }
        }));

        if (this.config.ctaBehavior === "cart") {
          window.location.href = `${routesRoot}cart`;
        } else {
          window.location.href = `${routesRoot}checkout`;
        }
      } catch (error) {
        if (this.status) this.status.textContent = error.message || "Something went wrong. Please try again.";
        this.busy = false;
        buttons.forEach((button) => button.removeAttribute("aria-busy"));
        this.render();
      }
    }
  }

  const init = () => {
    document.querySelectorAll("[data-wpb]").forEach((root) => new WelltaylPouchBuilder(root));
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
